/**
 * PATH: packages/kanban-host/src/allocation/useAllocationExplain.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-EXPLAIN-001
 * PURPOSE: React-Query wrapper around
 *          `KanbanApi.explainAllocation` so the "Why this board?"
 *          popover can render without each callsite duplicating
 *          query-key + enabled wiring.
 *
 * WHY: Module 3 of the EK-14 plan. The popover is opened lazily
 *      (right-click on the allocation chip → menu → "Why this
 *      board?"), so we MUST gate the fetch on `enabled` to avoid
 *      paying the network cost for every email row on the page.
 *      Centralising the query-key here also means cache
 *      invalidation in `useKanbanQuickAllocate` after a supersede
 *      can drop stale explainers in a single line.
 *
 * FLOW:
 *   useAllocationExplain({ api, allocationId, enabled })
 *     → useQuery(['kanban-allocation-explain', id], …)
 *     → returns { data, isLoading, isError, error, refetch }
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban  : KanbanApi + AllocationExplain
 *   - @tanstack/react-query
 */

import { useQuery } from '@tanstack/react-query'

import type { AllocationExplain, KanbanApi } from '@quotech/ui/kanban'

export interface UseAllocationExplainArgs {
  api: KanbanApi
  /** When null/undefined we never fire the request. */
  allocationId: string | null | undefined
  /** Gate the request — typically tied to popover open state. */
  enabled: boolean
}

export interface UseAllocationExplainResult {
  data: AllocationExplain | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  /** Force-refetch from the BE (drops the cached row). */
  refetch: () => void
}

/**
 * Pure: build the query-key. Exported so callers in other features
 * (e.g. a future "AI training audit" admin page) can invalidate the
 * same cache without importing the hook.
 */
export function buildAllocationExplainQueryKey(
  allocationId: string,
): ReadonlyArray<string> {
  return ['kanban-allocation-explain', allocationId]
}

export function useAllocationExplain(
  args: UseAllocationExplainArgs,
): UseAllocationExplainResult {
  const { api, allocationId, enabled } = args
  const result = useQuery<AllocationExplain, Error>({
    // We deliberately key on allocationId alone — the explain shape
    // is stable for a given allocation id; if another agent rejects
    // / confirms / supersedes we invalidate via the
    // `useKanbanQuickAllocate.buildInvalidationKeys()` list.
    queryKey: allocationId
      ? [...buildAllocationExplainQueryKey(allocationId)]
      : ['kanban-allocation-explain', 'no-id'],
    queryFn: async () => {
      if (!allocationId) {
        throw new Error('useAllocationExplain called without allocationId')
      }
      return api.explainAllocation(allocationId)
    },
    enabled: Boolean(allocationId) && enabled,
    // Refetch on mount only when more than 30s old; the popover
    // typically opens and closes within seconds.
    staleTime: 30_000,
  })
  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    error: (result.error as Error | null) ?? null,
    refetch: () => {
      void result.refetch()
    },
  }
}
